import { matchPath } from 'react-router'

import { RouteName } from '@/router'

/**
 * restriction dot-notation prefix → TeacherFeatureKey 매핑 (SSOT)
 *
 * TeacherFeatureKey 타입은 이 맵에서 추출된다. 새 권한 추가 시 여기에만 작성.
 */
const RESTRICTION_FEATURE_MAP = {
  'mathflat.teacher.signature_workbook': 'CONTENT_SIGNATURE_WORKBOOK',
  'mathflat.teacher.academy_group_workbook': 'CONTENT_ACADEMY_GROUP_WORKBOOK',
  'mathflat.teacher.scienceflat': 'CONTENT_SCIENCEFLAT',
  'mathflat.teacher.englishflat': 'CONTENT_ENGLISHFLAT',
  // worksheet
  'mathflat.teacher.custom_worksheet.academy_group': 'WORKSHEET_ACADEMY_GROUP',
  'mathflat.teacher.custom_worksheet.academy.copy_to_academy_group': 'WORKSHEET_ACADEMY_COPY',
  'mathflat.teacher.custom_worksheet.academy.my_list': 'WORKSHEET_ACADEMY_MY_LIST',
  'mathflat.teacher.custom_worksheet.academy.create': 'WORKSHEET_ACADEMY_CREATE',
  'mathflat.teacher.custom_worksheet.academy.edit': 'WORKSHEET_ACADEMY_EDIT',
  'mathflat.teacher.custom_worksheet.academy.delete': 'WORKSHEET_ACADEMY_DELETE',
  // workbook
  'mathflat.teacher.custom_workbook.academy_group': 'WORKBOOK_ACADEMY_GROUP',
  'mathflat.teacher.custom_workbook.academy.copy_to_academy_group': 'WORKBOOK_ACADEMY_COPY',
  'mathflat.teacher.custom_workbook.academy.create': 'WORKBOOK_ACADEMY_CREATE',
  'mathflat.teacher.custom_workbook.academy.edit': 'WORKBOOK_ACADEMY_EDIT',
  'mathflat.teacher.custom_workbook.academy.delete': 'WORKBOOK_ACADEMY_DELETE',
  'mathflat.teacher.custom_workbook.order': 'WORKBOOK_ORDER',
  'mathflat.teacher.exam': 'EXAM',
  'mathflat.teacher.shared_worksheet': 'SHARED_WORKSHEET',
  'mathflat.teacher.theme_worksheet.kmm': 'THEME_WORKSHEET_KMM',
  'mathflat.teacher.theme_worksheet.entrance_test': 'THEME_WORKSHEET_ENTRANCE_TEST',
  'mathflat.teacher.theme_worksheet.general_test': 'THEME_WORKSHEET_GENERAL_TEST',
  'mathflat.teacher.cart.preview': 'CART_PREVIEW',
  'mathflat.teacher.order.preview': 'ORDER_PREVIEW',
  'mathflat.teacher.order.parent_payment': 'ORDER_PARENT_PAYMENT',
  'mathflat.teacher.payment.preview': 'PAYMENT_PREVIEW',
  'mathflat.teacher.payment.read': 'PAYMENT_READ',
  'mathflat.teacher.settlement': 'SETTLEMENT',
  'mathflat.teacher.study_report_legacy': 'STUDY_REPORT_LEGACY',
  'mathflat.teacher.cas': 'CAS',
  'mathflat.teacher.lesson.guest': 'LESSON_GUEST',
  'mathflat.teacher.daily_worksheet': 'DAILY_WORKSHEET',
  'mathflat.teacher.creverse_guest': 'CREVERSE_GUEST',
  'mathflat.teacher.creverse_class_sync': 'CREVERSE_CLASS_SYNC',
  'mathflat.teacher.creverse.report.share': 'CREVERSE_REPORT_SHARE',
  'mathflat.teacher.attendance': 'ATTENDANCE',
  'mathflat.teacher.tuition': 'TUITION',
  'mathflat.teacher.sms': 'SMS',
  'mathflat.teacher.worksheet.download': 'WORKSHEET_DOWNLOAD',
  'mathflat.teacher.parent_app_announcement': 'PARENT_APP_ANNOUNCEMENT',
  'mathflat.teacher.faq': 'FAQ',
  'mathflat.teacher.school_exam_zone': 'SCHOOL_EXAM_ZONE',
  'mathflat.teacher.customer_notice': 'CUSTOMER_NOTICE',
  'mathflat.teacher.vod_column': 'VOD_COLUMN',
  'mathflat.teacher.student_manage.edit': 'STUDENT_MANAGE_EDIT',
  'mathflat.teacher.teacher_manage.edit': 'TEACHER_MANAGE_EDIT',
  'mathflat.teacher.class_manage.edit': 'CLASS_MANAGE_EDIT',
  'mathflat.teacher.academy_info.edit': 'ACADEMY_INFO_EDIT',
  'mathflat.teacher.academy_info.view': 'ACADEMY_INFO_VIEW',
  'mathflat.teacher.academy_logo.update.default': 'ACADEMY_LOGO_UPDATE_DEFAULT',
  'mathflat.teacher.academy_logo.update': 'ACADEMY_LOGO_UPDATE',
  'mathflat.teacher.teacher_info.password.update': 'TEACHER_PASSWORD_UPDATE',
  // access 축 — BE 가 `mathflat.teacher.teacher_manage.*` 처럼 와일드카드로 발행하면 하위 .edit 까지
  // 함께 제한되고, `.edit` 단건(정확 일치) 발행은 이 access 키를 건드리지 않는다.
  'mathflat.teacher.teacher_manage': 'TEACHER_MANAGE',
  'mathflat.teacher.academy_switch': 'ACADEMY_SWITCH',
  'mathflat.teacher.laboratory': 'LABORATORY',
  'mathflat.teacher.arithmetic_worksheet': 'ARITHMETIC_WORKSHEET',
  'mathflat.teacher.my_db': 'MY_DB',
  'mathflat.teacher.worksheet.hwp.download': 'WORKSHEET_HWP_DOWNLOAD',
  'mathflat.teacher.essay_scoring': 'ESSAY_SCORING',
} as const satisfies Record<string, string>

export type TeacherFeatureKey =
  (typeof RESTRICTION_FEATURE_MAP)[keyof typeof RESTRICTION_FEATURE_MAP]

/** TeacherFeatureKey → restriction prefix 역참조 */
const FEATURE_RESTRICTION_MAP: Record<TeacherFeatureKey, string> = Object.fromEntries(
  Object.entries(RESTRICTION_FEATURE_MAP).map(([prefix, key]) => [key, prefix]),
) as Record<TeacherFeatureKey, string>

/**
 * FE SSOT: feature key → route 게이팅 설정
 */
const ROUTE_PERMISSION_MAP: Partial<Record<TeacherFeatureKey, ValueOf<typeof RouteName>[]>> = {
  CONTENT_SIGNATURE_WORKBOOK: [
    RouteName.signatureWorkbook,
    RouteName.makeWorksheetBySignatureWorkbook,
    RouteName.makeSignatureWorkbook,
    RouteName.makeSignatureWorkbookProductList,
    RouteName.makeSignatureWorkbookCover,
    RouteName.makeSignatureWorkbookCoverCreate,
    RouteName.makeSignatureWorkbookCoverEditOrCopyEdit,
    RouteName.signatureWorkbookViewer,
    RouteName.signatureWorkbookViewerByStudent,
  ],
  CONTENT_ACADEMY_GROUP_WORKBOOK: [
    RouteName.academyGroupWorkbook,
    RouteName.academyGroupWorkbookViewer,
    RouteName.academyGroupWorkbookViewerByStudent,
  ],
  // academyShareScopeWorksheet·academyShareScopeWorkbook 은 restriction 없음 — 항상 접근 가능
  WORKSHEET_ACADEMY_GROUP: [RouteName.academyGroupShareScopeWorksheet],
  WORKBOOK_ACADEMY_GROUP: [RouteName.academyGroupShareScopeWorkbook],
  EXAM: [
    RouteName.kmmRoot,
    RouteName.kmmOverview,
    RouteName.kmmGuideline,
    RouteName.kmmRegister,
    RouteName.kmmHighSchoolRegister,
    RouteName.kmmApplyMaterials,
    RouteName.weizmannExam,
    RouteName.examGroupScoring,
    RouteName.kmmAutoAssigningManage,
  ],
  SHARED_WORKSHEET: [RouteName.share, RouteName.shareWorksheetSetting],
  MY_DB: [RouteName.myDB, RouteName.myDBEdit],
  ARITHMETIC_WORKSHEET: [RouteName.makeWorksheetByArithmetic],
  THEME_WORKSHEET_KMM: [RouteName.kmmExamWorksheet],
  THEME_WORKSHEET_ENTRANCE_TEST: [RouteName.themeEntranceTest],
  THEME_WORKSHEET_GENERAL_TEST: [RouteName.generalTest],
  STUDY_REPORT_LEGACY: [RouteName.createLessonReport, RouteName.updateLessonReport],
  PAYMENT_READ: [RouteName.myStatus],
  SETTLEMENT: [RouteName.mySettlement],
  CAS: [
    RouteName.cas,
    RouteName.makeCas,
    RouteName.lessonCas,
    RouteName.lessonCasStudent,
    RouteName.lessonCasGrade,
    RouteName.lessonCasClass,
    RouteName.casGroupScoringByLessonClass,
    RouteName.casGroupScoringByGrade,
  ],
  CREVERSE_GUEST: [
    RouteName.lessonGuestEntranceTest,
    RouteName.lessonGuestEntranceTestWorksheet,
    RouteName.lessonGuestEntranceTestWorksheetStudent,
    RouteName.lessonGuestEntranceTestCas,
    RouteName.lessonGuestEntranceTestCasStudent,
  ],
  DAILY_WORKSHEET: [
    RouteName.lessonDailyLearning,
    RouteName.lessonDailyLearningLiveLesson,
    RouteName.lessonDailyLearningLiveLessonStudent,
    RouteName.studentTodayLearningManage,
  ],
  ATTENDANCE: [
    RouteName.attendanceManage,
    RouteName.attendanceManageGrade,
    RouteName.attendanceManageClass,
    RouteName.attendanceManageStudent,
  ],
  SMS: [RouteName.smsManage, RouteName.smsSend, RouteName.smsHistory],
  TEACHER_MANAGE: [RouteName.teacherManage],
  LABORATORY: [RouteName.laboratoryManage],
  ESSAY_SCORING: [RouteName.essayAiScoringManage],
  PARENT_APP_ANNOUNCEMENT: [RouteName.parentAppAnnouncementManage],
  FAQ: [RouteName.customerFaq],
  SCHOOL_EXAM_ZONE: [RouteName.schoolExamZone],
  CUSTOMER_NOTICE: [RouteName.customerNotice],
  VOD_COLUMN: [RouteName.vodColumn, RouteName.vodColumnList, RouteName.vodColumnDetail],
  TUITION: [RouteName.AcademyTuitionManage],
}

/**
 * FE SSOT: 공유 부모 경로 → 그 아래 화면들의 feature key
 *
 * 부모 경로는 하위가 전부 막혔을 때만 차단한다. 한쪽만 막힌 학원이 부모 경로로 들어왔을 때
 * 살아 있는 하위 화면으로 넘어갈 길을 남겨야 하기 때문이다.
 */
const SHARED_PARENT_ROUTES: readonly {
  parent: ValueOf<typeof RouteName>
  children: readonly TeacherFeatureKey[]
}[] = [{ parent: RouteName.attendance, children: ['ATTENDANCE', 'TUITION'] }]

/**
 * FE SSOT: feature key → workbook type 매핑
 */
const CONTENT_PERMISSION_MAP: Partial<Record<TeacherFeatureKey, string[]>> = {
  CONTENT_SIGNATURE_WORKBOOK: ['CUSTOM_SIGNATURE'],
  CONTENT_ACADEMY_GROUP_WORKBOOK: ['ACADEMY_GROUP'],
}

/**
 * restriction 패턴이 대상 경로에 매칭되는지 확인
 *
 * - "mathflat.teacher.signature-workbook.*" → prefix + 하위 모두 매칭
 * - "mathflat.teacher.signature-workbook.rent" → 정확히 일치만 매칭
 */
function matchRestriction(pattern: string, target: string): boolean {
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2)
    return target === prefix || target.startsWith(prefix + '.')
  }
  return pattern === target
}

export class FeaturePermission {
  private readonly _restrictions: readonly string[]

  constructor(restrictions: string[]) {
    this._restrictions = restrictions
  }

  /** 주어진 restrictions가 현재 보유한 것과 (순서 무관) 동일한지 여부 */
  hasSameRestrictions(restrictions: string[]): boolean {
    if (this._restrictions.length !== restrictions.length) return false
    const current = new Set(this._restrictions)
    return restrictions.every((restriction) => current.has(restriction))
  }

  /** 대상 경로가 restriction에 매칭되는지 여부 */
  isRestricted(target: string): boolean {
    return this._restrictions.some((pattern) => matchRestriction(pattern, target))
  }

  /** TeacherFeatureKey가 허용되는지 여부. restriction에 없으면 허용 (blacklist) */
  isAllowed(key: TeacherFeatureKey): boolean {
    const prefix = FEATURE_RESTRICTION_MAP[key]
    if (!prefix) return true
    return !this.isRestricted(prefix)
  }

  /**
   * 주어진 workbook type이 허용되는지 여부.
   * CONTENT_PERMISSION_MAP에 매핑된 key가 restricted이면 false.
   */
  isWorkbookTypeAllowed(type: string): boolean {
    return (Object.entries(CONTENT_PERMISSION_MAP) as [TeacherFeatureKey, string[]][]).every(
      ([key, types]) => !types.includes(type) || this.isAllowed(key),
    )
  }

  /**
   * 주어진 pathname이 접근 허용되는지 여부.
   *
   * ROUTE_PERMISSION_MAP에 선언된 key 중 막힌 key의 route와 매칭되면 false를 반환한다.
   *
   * deniedKeys 는 restriction 이 아닌 축(권한 트리의 브랜드·롤)이 막는 key 를 받는다.
   * 두 축을 함께 봐야 메뉴만 숨고 URL 직접 진입은 열려 있는 구멍이 생기지 않는다.
   */
  isRouteAllowed(pathname: string, deniedKeys: readonly TeacherFeatureKey[] = []): boolean {
    const isDenied = (key: TeacherFeatureKey) => deniedKeys.includes(key) || !this.isAllowed(key)

    const blockedSharedParent = SHARED_PARENT_ROUTES.some(
      ({ parent, children }) =>
        matchPath(pathname, { path: parent, exact: true }) && children.every(isDenied),
    )
    if (blockedSharedParent) return false

    return (
      Object.entries(ROUTE_PERMISSION_MAP) as [TeacherFeatureKey, ValueOf<typeof RouteName>[]][]
    ).every(
      ([key, routes]) =>
        !routes?.length ||
        !isDenied(key) ||
        !routes.some((route) => matchPath(pathname, { path: route, exact: true })),
    )
  }
}
